/**
 *
 * AuthenticatorWidget Component
 *
 */

import {
  Button,
  CircularProgress,
  Enums,
  Skeleton,
  stylize,
} from "@envestnet/envreact-component-library";
import classnames from "classnames";
import AppIconCircle from "images/AppIconCircle";
import EmailIconCircle from "images/EmailIconCircle";
import OktaIconCircle from "images/OktaIconCircle";
import PhoneIconCircle from "images/PhoneIconCircle";
import React from "react";
import { AuthenticatorConfiguration } from "types/Mfa";
import withIntl from "utils/withIntl";
import messages from "./messages";
import styles, { Styles } from "./styles";

const { ButtonSize, ButtonStyle } = Enums;

interface InjectedProps extends Styles {
  /** Internal - injected from withIntl */
  formatMessage: FormatMessage;

  /** Internal - injected from WithStyles */
  innerRef?: React.RefObject<HTMLDivElement>;
}

export interface AuthenticatorWidgetProps {
  /** CSS class name to be applied to top level component */
  className?: string;

  /** Inline style object to be applied to top level component */
  style?: React.CSSProperties;

  /** Description of this prop */
  type: string;

  configurationStatus: AuthenticatorConfiguration;

  goToOktaVerifyScreen?: any;

  goToAppAuthenticatorScreen?: any;

  goToAppPhoneScreen?: any;

  goToEmailOtpScreen?: () => void;

  goToEnvAppScreen?: () => void;

  isLoading?: boolean;

  isSpinner?: boolean;
}

type Props = InjectedProps & AuthenticatorWidgetProps;

const AuthenticatorWidget = (props: Props) => {
  const {
    classes,
    style,
    className,
    formatMessage,
    innerRef,
    type,
    configurationStatus,
    goToAppPhoneScreen,
    goToOktaVerifyScreen,
    goToAppAuthenticatorScreen,
    goToEmailOtpScreen,
    goToEnvAppScreen,
    isLoading,
    isSpinner,
  } = props;

  const typeChecker = React.useCallback(
    (arg) => {
      let typeComponent;
      switch (arg) {
        case "email":
          typeComponent = <EmailIconCircle />;
          break;
        case "app":
          typeComponent = <AppIconCircle />;
          break;
        case "envapp":
          typeComponent = <AppIconCircle />;
          break;
        case "phone":
          typeComponent = <PhoneIconCircle />;
          break;
        case "okta":
          typeComponent = <OktaIconCircle />;
          break;
        default:
          break;
      }
      return typeComponent;
    },
    [type]
  );

  const configurationChecker = React.useCallback(
    (arg, t) => {
      let configComponent;
      switch (arg) {
        case AuthenticatorConfiguration.Initial:
          configComponent = (
            <Button
              buttonStyle={ButtonStyle.Secondary}
              buttonSize={ButtonSize.Regular}
              data-testid="config-button"
              className={classnames(
                classes.submitButton,
                "no-border-radius",
                "header-text-font-family"
              )}
              name={formatMessage(messages[`${type}Button`])}
              onClick={() => {
                switch (t) {
                  case "app":
                    goToAppAuthenticatorScreen();
                    break;

                  case "okta":
                    goToOktaVerifyScreen();
                    break;

                  case "phone":
                    goToAppPhoneScreen();
                    break;

                  case "email":
                    if (goToEmailOtpScreen) goToEmailOtpScreen();
                    break;

                  case "envapp":
                    if (goToEnvAppScreen) goToEnvAppScreen();
                    break;

                  default:
                    break;
                }
              }}
            />
          );
          break;
        case AuthenticatorConfiguration.Remove:
          configComponent = (
            <Button
              buttonStyle={ButtonStyle.Link}
              buttonSize={ButtonSize.Regular}
              className={classnames(
                classes.removeButton,
                "no-border-radius",
                "header-text-font-family"
              )}
              name={formatMessage(messages[`${type}ButtonRemove`])}
              data-testid="remove-mfa"
              onClick={() => {
                switch (t) {
                  case "app":
                    goToAppAuthenticatorScreen();
                    break;

                  case "okta":
                    goToOktaVerifyScreen();
                    break;

                  case "phone":
                    goToAppPhoneScreen();
                    break;

                  case "envapp":
                    if (goToEnvAppScreen) goToEnvAppScreen();
                    break;

                  case "email":
                    if (goToEmailOtpScreen) goToEmailOtpScreen();
                    break;

                  default:
                    break;
                }
              }}
            />
          );
          break;
        case AuthenticatorConfiguration.Done:
          configComponent = (
            <>
              <div
                className={classnames(
                  classes.configurationStatus,
                  "header-font-family"
                )}
                data-testid="mfa-config-done"
              >
                {formatMessage(messages[`${type}Status`])}
              </div>
              <div
                className={classnames(
                  classes.configurationStatusDesc,
                  "header-text-font-family"
                )}
              >
                {formatMessage(messages[`${type}StatusDesc`])}
              </div>
            </>
          );
          break;
        case AuthenticatorConfiguration.Error:
          configComponent = (
            <Button
              buttonStyle={ButtonStyle.SecondaryWarning}
              buttonSize={ButtonSize.Regular}
              data-testid="mfa-error"
              className={classnames(
                classes.submitButton,
                "no-border-radius",
                "header-text-font-family"
              )}
              name={formatMessage(messages[`${type}ErrorButton`])}
              onClick={() => {}}
            />
          );
          break;
        default:
          break;
      }
      return configComponent;
    },
    [configurationStatus]
  );

  return (
    <div
      ref={innerRef}
      className={classnames(className, classes.container)}
      style={style}
    >
      <div>{typeChecker(type)}</div>
      <div className={classes.rightContainer}>
        <div className={classnames(classes.headerStyle, "header-font-family")}>
          {formatMessage(messages[`${type}Header`])}
        </div>
        <div
          className={classnames(classes.descStyle, "header-text-font-family")}
        >
          {formatMessage(messages[`${type}Desc`])}
        </div>
        {isLoading ? (
          <>
            {isSpinner ? (
              <CircularProgress size={24} style={{ marginTop: 20 }} />
            ) : (
              <Skeleton width={180} height={20} style={{ marginTop: 20 }} />
            )}
          </>
        ) : (
          configurationChecker(configurationStatus, type)
        )}
      </div>
    </div>
  );
};

export default withIntl(stylize<Props>(styles)(AuthenticatorWidget));
